import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../config";
import NotificationCart from "./NotificationCart";
import BackButton from "./BackButton";
import CustomerLogin from "./CustomerLogin";
import apiClient from "../api/CustomerClient";

const CustomerDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const cart = location.state?.cart || [];
  const restaurant_id =
    location.state?.restaurant_id ||
    new URLSearchParams(location.search).get("restaurant_id");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [formMode, setFormMode] = useState("login");
  const [isLoginVisible, setLoginVisible] = useState(false);
  const [userData, setUserData] = useState(() => {
    const storedData = localStorage.getItem("customerData");
    return storedData && storedData !== "undefined"
      ? JSON.parse(storedData)
      : null;
  });
  const [orderType, setOrderType] = useState("dine-in");
  const [table_no, settable_number] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationStatus, setNotificationStatus] = useState("");
  const [isNotification, setIsNotification] = useState(false);
  const [geoLocation, setGeoLocation] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [restaurantDetail, setRestaurantDetail] = useState("");
  const [showRestaurantDetails, setShowRestaurantDetails] = useState(false);
  const [loginFormData, setLoginFormData] = useState({
    email: "",
    password: "",
  });

  // Desktop event handlers
  const handleRestaurantDetailsHover = () => {
    setShowRestaurantDetails(true);
  };

  const handleRestaurantDetailsLeave = () => {
    setShowRestaurantDetails(false);
  };

  // Mobile event handler
  const handleRestaurantDetailsClick = () => {
    setShowRestaurantDetails((prevState) => !prevState);
  };

  useEffect(() => {
    const handleScroll = () => {
      const headerHeight = 100;
      setIsScrolled(window.scrollY > headerHeight);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    // Handle user authentication
    const storedUserData = localStorage.getItem("customerData");
    const token = localStorage.getItem("CustomerToken");
    const storedLocation = localStorage.getItem("customerLocation");

    if (storedUserData && token) {
      try {
        const parsedData = JSON.parse(storedUserData);
        setUserData(parsedData);
        setIsLoggedIn(true);
      } catch (error) {
        console.error("Error parsing stored user data:", error);
        localStorage.removeItem("customerData");
        localStorage.removeItem("CustomerToken");
        setUserData(null);
        setIsLoggedIn(false);
      }
    } else {
      setUserData(null);
      setIsLoggedIn(false);
    }

    // Handle geolocation
    if (storedLocation) {
      setGeoLocation(JSON.parse(storedLocation));
    } else {
      getCustomerLocation();
    }

    fetchRestaurantDetails();

    // Cleanup function
    return () => {
      setUserData(null);
      setIsLoggedIn(false);
    };
  }, []);

  useEffect(() => {
    const checkTokenExpiration = () => {
      const token = localStorage.getItem("CustomerToken");
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split(".")[1]));
          const expirationTime = payload.exp * 1000;

          if (Date.now() >= expirationTime) {
            localStorage.removeItem("customerData");
            localStorage.removeItem("CustomerToken");
            setUserData(null);
            setIsLoggedIn(false);
            showNotification("Session expired. Please login again.", "info");
          }
        } catch (error) {
          console.error("Error checking token expiration:", error);
        }
      }
    };

    checkTokenExpiration();
    const interval = setInterval(checkTokenExpiration, 60000);

    return () => clearInterval(interval);
  }, []);

  const getCustomerLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setGeoLocation({ latitude, longitude });
        },
        (err) => {
          showNotification("Failed to get location", "error");
          console.error(err);
        }
      );
    }
  };

  const showNotification = (message, status) => {
    setNotificationMessage(message);
    setNotificationStatus(status);
    setIsNotification(true);
    setTimeout(() => {
      setIsNotification(false);
    }, 9000);
  };

  const fetchRestaurantDetails = async () => {
    try {
      const response = await axios.get(
        `${config.API_BASE_URL}/restaurant/get-restaurant/${restaurant_id}`
      );
      console.log(response.data.data);
      setRestaurantDetail(response.data.data);
    } catch (error) {
      showNotification("Error fetching restaurant details", "error");
    }
  };

  const handleOrderTypeChange = (type) => {
    setOrderType(type);
    settable_number("");
  };

  const calculateTotal = () => {
    return cart.reduce((total, item) => {
      // Ensure price is treated as a number
      const price =
        typeof item.price === "string"
          ? parseFloat(item.price.replace(/[^0-9.-]/g, ""))
          : Number(item.price);

      // Ensure quantity is treated as a number
      const quantity = Number(item.quantity);

      return total + price * quantity;
    }, 0);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setErrorMessage(""); // Clear error message before submitting
    try {
      const response = await axios.post(
        `${config.API_BASE_URL}/customer/login`,
        loginFormData
      );
      setUserData(response.data);
      setIsLoggedIn(true);
      localStorage.setItem("customerData", JSON.stringify(response.data));
      localStorage.setItem("CustomerToken", response.data.token);
    } catch (error) {
      console.error("Error logging in:", error);
      setErrorMessage("Invalid email or password");
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setErrorMessage(""); // Clear error message before submitting
    if (!termsAccepted) {
      setErrorMessage("Please accept the Terms and Conditions");
      return;
    }
    if (userData.password !== passwordConfirmation) {
      setErrorMessage("Passwords do not match");
      return;
    }
    try {
      const response = await axios.post(
        `${config.API_BASE_URL}/customer/create`,
        { ...userData, restaurant_id: restaurant_id }
      );
      setUserData(response.data.data);
      localStorage.setItem("customerData", JSON.stringify(response.data.data));
      localStorage.setItem("CustomerToken", response.data.data.token);
      setIsLoggedIn(true);
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        "Error creating account. Please try again.";
      setErrorMessage(errorMessage);
    }
  };

  const handleGuestLogin = async (e) => {
    e.preventDefault();
    setErrorMessage(""); // Clear error message before submitting
    if (!termsAccepted) {
      setErrorMessage("Please accept the Terms and Conditions");
      return;
    }
    try {
      const response = await axios.post(
        `${config.API_BASE_URL}/customer/create`,
        { ...userData, is_guest: true, restaurant_id: restaurant_id }
      );
      console.log("Token received:", response.data.data.token);
      setUserData(response.data.data);
      localStorage.setItem("customerData", JSON.stringify(response.data.data));
      localStorage.setItem("CustomerToken", response.data.data.token);
      setIsLoggedIn(true);
    } catch (error) {
      setErrorMessage(
        error.response?.data?.message ||
          "Error creating account. Please try again."
      );
    }
  };

  const [showDropdown, setShowDropdown] = useState(false);
  const showLoginButtonPaths = ["/menu-display", "/cart", "/customer_details"];

  const handleLoginClick = () => {
    setUserData(null);
    setLoginVisible(true);
  };
  const handleCloseLogin = () => setLoginVisible(false);
  const handleLoginSuccess = (data) => {
    setUserData(data);
    setIsLoggedIn(true);
    setLoginVisible(false);
    window.location.reload();
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(
        `${config.API_BASE_URL}/customer/forgot-password`,
        { email: resetEmail }
      );
      showNotification(
        response.data.message || "Password reset link sent",
        "success"
      );
      setFormMode("login");
      setErrorMessage("");
    } catch (error) {
      showNotification(
        error.response?.data?.message || "Error resetting password",
        "error"
      );
    }
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!isLoggedIn && formMode !== "guestLogin") {
      showNotification("Please log in or continue as guest", "error");
      return;
    }

    if (orderType === "dine-in" && !table_no) {
      showNotification(
        "Please select a table number for dine-in order",
        "error"
      );
      return;
    }

    if (orderType === "pickup" && !geoLocation) {
      getCustomerLocation();
      return;
    }

    setIsSubmitting(true);
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const formattedTime = `${hours}:${minutes}`;
    const token = localStorage.getItem("CustomerToken");

    const orderItems = cart.map((item) => ({
      menu_item_id: item.menu_id,
      quantity: item.quantity,
    }));

    try {
      const response = await apiClient.post(
        `/customer/place-order`,
        {
          restaurant_id: Number(restaurant_id),
          customer_details: userData,
          order_items: orderItems,
          order_type: orderType,
          table_no: orderType === "dine-in" ? table_no : null,
          is_guest: formMode === "guestLogin",
          order_time: formattedTime,
          customer_location: {
            latitude: geoLocation?.latitude,
            longitude: geoLocation?.longitude,
          },
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 201) {
        localStorage.removeItem("currentCart");
        navigate(
          `/customer/order-status?orderId=${response.data.order_id}&restaurant_id=${restaurant_id}`
        );
      }
    } catch (error) {
      showNotification(
        error.response?.data?.message || "Error placing order",
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-4 md:mt-5">
      <BackButton />
      <>
        <div className="max-w-7xl mx-auto px-4 mt-18 md:mt-15">
          <div className="max-w-6xl mx-auto relative">
            <div
              className={`${
                isScrolled ? "fixed top-0 shadow-lg" : "relative shadow-sm"
              } left-0 right-0 z-40 bg-white/95 backdrop-blur-sm border-b border-gray-100 transition-all duration-300`}
              onMouseEnter={handleRestaurantDetailsHover}
              onMouseLeave={handleRestaurantDetailsLeave}
              onClick={handleRestaurantDetailsClick}
            >
              <div className="max-w-7xl mx-auto p-4">
                <div className="flex flex-col gap-3">
                  {/* Top row with restaurant name and login */}
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      {restaurantDetail?.logo && (
                        <img 
                          src={restaurantDetail.logo} 
                          alt={restaurantDetail.restaurant_name}
                          className="w-10 h-10 rounded-full object-cover border border-gray-200"
                        />
                      )}
                      <h1 className="text-2xl font-bold text-gray-900 truncate max-w-[60%]">
                        {restaurantDetail?.restaurant_name}
                      </h1>
                    </div>

                    {showLoginButtonPaths.includes(location.pathname) &&
                      (userData && localStorage.getItem("CustomerToken") ? (
                        <div className="relative">
                          <button
                            onClick={() => setShowDropdown(!showDropdown)}
                            className="flex items-center gap-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200"
                          >
                            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                              {userData.customer_name?.charAt(0).toUpperCase()}
                            </div>
                            <span className="text-sm font-medium text-gray-700 truncate max-w-[100px]">
                              {userData.customer_name}
                            </span>
                            <svg
                              className="w-4 h-4 text-gray-500 flex-shrink-0"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          </button>
                          {showDropdown && (
                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                              <button
                                onClick={() => {
                                  navigate("/customer_orders", {
                                    state: { customerId: userData.customer_id },
                                  });
                                  setShowDropdown(false);
                                }}
                                className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                </svg>
                                Order Status
                              </button>
                              <button
                                onClick={() => {
                                  localStorage.removeItem("customerData");
                                  localStorage.removeItem("CustomerToken");
                                  setUserData(null);
                                  setIsLoggedIn(false);
                                  setShowDropdown(false);
                                  setFormMode("login");
                                  setLoginFormData({ email: "", password: "" });
                                }}
                                className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                </svg>
                                Logout
                              </button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <button
                          onClick={handleLoginClick}
                          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          Login
                        </button>
                      ))}
                  </div>

                  {/* Bottom row with address details */}
                  <div
                    className={`text-sm text-gray-600 overflow-x-auto transition-all duration-300 ${
                      showRestaurantDetails ? "block opacity-100" : "hidden opacity-0"
                    }`}
                  >
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="flex items-center gap-1">
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="truncate">
                          {restaurantDetail?.address}
                        </span>
                      </div>
                      <span className="text-gray-300">•</span>
                      <span className="truncate">{restaurantDetail?.city}</span>
                      <span className="text-gray-300">•</span>
                      <span className="truncate">
                        {restaurantDetail?.state}
                      </span>
                      {restaurantDetail?.phone && (
                        <>
                          <span className="text-gray-300">•</span>
                          <div className="flex items-center gap-1">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                            <span>
                              {restaurantDetail.phone.replace(
                                /(\d{3})(\d{3})(\d{4})/,
                                "$1-$2-$3"
                              )}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Login Modal */}
        {isLoginVisible && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 transform transition-all">
              <CustomerLogin
                onClose={handleCloseLogin}
                onLoginSuccess={handleLoginSuccess}
              />
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white py-8 px-4 mt-16 sm:mt-24">
          <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
            {/* Header Section */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-white">
              <h1 className="text-3xl font-bold mb-2">Customer Details</h1>
              <p className="text-blue-100">Complete your order information</p>
            </div>

            <div className="p-8">
              {isNotification && (
                <NotificationCart
                  message={notificationMessage}
                  status={notificationStatus}
                />
              )}

              {isLoggedIn ? (
                <div className="space-y-8">
                  {/* Welcome Section */}
                  <div className="text-center bg-blue-50 rounded-xl p-6 border border-blue-100">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                      Welcome back, {userData?.customer_name || "Guest"}!
                    </h2>
                    <p className="text-gray-600">Ready to complete your order</p>
                  </div>

                  {/* Order Type Selection */}
                  <div className="space-y-4">
                    <h3 className="text-xl font-semibold text-gray-900">
                      Select Your Order Type
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        type="button"
                        className={`p-4 rounded-xl border-2 transition-all ${
                          orderType === "pickup"
                            ? "border-blue-500 bg-blue-50 text-blue-700 shadow-lg shadow-blue-500/25"
                            : "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:shadow-md"
                        }`}
                        onClick={() => handleOrderTypeChange("pickup")}
                      >
                        <div className="text-center">
                          <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                          </svg>
                          <span className="font-semibold">Pickup</span>
                        </div>
                      </button>
                      <button
                        type="button"
                        className={`p-4 rounded-xl border-2 transition-all ${
                          orderType === "dine-in"
                            ? "border-blue-500 bg-blue-50 text-blue-700 shadow-lg shadow-blue-500/25"
                            : "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:shadow-md"
                        }`}
                        onClick={() => handleOrderTypeChange("dine-in")}
                      >
                        <div className="text-center">
                          <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                          <span className="font-semibold">Dine In</span>
                        </div>
                      </button>
                    </div>
                  </div>

                  {/* Table Selection for Dine-in */}
                  {orderType === "dine-in" && restaurantDetail && (
                    <div className="space-y-3">
                      <label className="block text-lg font-semibold text-gray-900">
                        Table Number
                      </label>
                      <select
                        className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white"
                        value={table_no}
                        onChange={(e) => settable_number(e.target.value)}
                        required
                      >
                        <option value="">Select your table</option>
                        {[...Array(restaurantDetail.number_of_tables)].map(
                          (_, index) => (
                            <option key={index + 1} value={index + 1}>
                              Table {index + 1}
                            </option>
                          )
                        )}
                      </select>
                    </div>
                  )}

                  {/* Order Summary */}
                  <div className="space-y-4 bg-gray-50 rounded-xl p-6 border border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-900">
                      Order Summary
                    </h2>
                    <div className="space-y-3">
                      {cart.map((item) => (
                        <div
                          key={item.menu_id}
                          className="flex justify-between items-center bg-white p-4 rounded-lg border border-gray-100"
                        >
                          <div className="flex-1 min-w-0">
                            <span className="font-medium text-gray-900">
                              {item.quantity} x {item.item_name}
                            </span>
                          </div>
                          <span className="font-semibold text-gray-900 whitespace-nowrap">
                            ${(item.price * item.quantity).toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="border-t pt-4 flex justify-between items-center">
                      <span className="text-lg font-bold text-gray-900">Sub-Total:</span>
                      <span className="text-2xl font-bold text-blue-600">
                        ${calculateTotal().toFixed(2)}
                      </span>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <form onSubmit={handleSubmit}>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={`w-full py-4 px-6 rounded-xl font-semibold text-lg transition-all ${
                        isSubmitting
                          ? "bg-gray-400 cursor-not-allowed text-white"
                          : "bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                      }`}
                    >
                      {isSubmitting ? (
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Processing Order...
                        </div>
                      ) : (
                        "Place Order"
                      )}
                    </button>
                  </form>
                </div>
              ) : (
                <div className="space-y-8">
                  {/* Authentication Forms */}
                  {formMode === "login" && (
                    <div className="space-y-6">
                      <div className="text-center">
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome Back</h2>
                        <p className="text-gray-600">Sign in to your account</p>
                      </div>
                      
                      {errorMessage && (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                          <p className="text-red-700 text-sm">{errorMessage}</p>
                        </div>
                      )}
                      
                      <form onSubmit={handleLogin} className="space-y-4">
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Email Address
                          </label>
                          <input
                            type="email"
                            className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                            value={loginFormData?.email}
                            onChange={(e) => {
                              setLoginFormData({
                                ...loginFormData,
                                email: e.target.value,
                              });
                              setErrorMessage("");
                            }}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Password
                          </label>
                          <input
                            type="password"
                            className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                            value={loginFormData.password}
                            onChange={(e) => {
                              setLoginFormData({
                                ...loginFormData,
                                password: e.target.value,
                              });
                              setErrorMessage("");
                            }}
                            required
                          />
                        </div>
                        <button
                          type="submit"
                          className="w-full bg-blue-600 text-white py-4 px-6 rounded-xl hover:bg-blue-700 transition-colors font-semibold text-lg"
                        >
                          Sign In
                        </button>
                      </form>
                      
                      <div className="space-y-3">
                        <button
                          type="button"
                          onClick={() => {
                            setFormMode("signup");
                            setErrorMessage("");
                          }}
                          className="w-full bg-gray-100 text-gray-800 py-3 px-4 rounded-xl hover:bg-gray-200 transition-colors font-medium"
                        >
                          Create New Account
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setFormMode("guestLogin");
                            setErrorMessage("");
                          }}
                          className="w-full border-2 border-gray-300 text-gray-700 py-3 px-4 rounded-xl hover:bg-gray-50 transition-colors font-medium"
                        >
                          Continue as Guest
                        </button>
                      </div>
                    </div>
                  )}

                  {formMode === "forgotPassword" && (
                    <div className="space-y-6">
                      <div className="text-center">
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Reset Password</h2>
                        <p className="text-gray-600">Enter your email to receive a reset link</p>
                      </div>
                      
                      {errorMessage && (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                          <p className="text-red-700 text-sm">{errorMessage}</p>
                        </div>
                      )}
                      
                      <form onSubmit={handleForgotPassword} className="space-y-4">
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Email Address
                          </label>
                          <input
                            type="email"
                            className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                            value={resetEmail}
                            onChange={(e) => setResetEmail(e.target.value)}
                            required
                          />
                        </div>
                        <div className="space-y-3">
                          <button
                            type="submit"
                            className="w-full bg-blue-600 text-white py-4 px-6 rounded-xl hover:bg-blue-700 transition-colors font-semibold text-lg"
                          >
                            Send Reset Link
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setFormMode("login");
                              setErrorMessage("");
                            }}
                            className="w-full bg-gray-100 text-gray-800 py-3 px-4 rounded-xl hover:bg-gray-200 transition-colors font-medium"
                          >
                            Back to Login
                          </button>
                        </div>
                      </form>
                    </div>
                  )}

                  {formMode === "signup" && (
                    <div className="space-y-6">
                      <div className="text-center">
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Create Account</h2>
                        <p className="text-gray-600">Join us for a better experience</p>
                      </div>
                      
                      {errorMessage && (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                          <p className="text-red-700 text-sm">{errorMessage}</p>
                        </div>
                      )}
                      
                      <form onSubmit={handleSignup} className="space-y-4">
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Full Name
                          </label>
                          <input
                            type="text"
                            className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                            value={userData?.customer_name}
                            onChange={(e) =>
                              setUserData({
                                ...userData,
                                customer_name: e.target.value,
                              })
                            }
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Email Address
                          </label>
                          <input
                            type="email"
                            className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                            value={userData?.email}
                            onChange={(e) =>
                              setUserData({
                                ...userData,
                                email: e.target.value,
                              })
                            }
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Phone Number
                          </label>
                          <input
                            type="tel"
                            className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                            value={userData?.phone_number}
                            onChange={(e) => {
                              const value = e.target.value
                                .replace(/\D/g, "")
                                .slice(0, 10);
                              setUserData({ ...userData, phone_number: value });
                            }}
                            pattern="[0-9]{10}"
                            maxLength="10"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Password
                          </label>
                          <input
                            type="password"
                            className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                            value={userData?.password}
                            onChange={(e) =>
                              setUserData({
                                ...userData,
                                password: e.target.value,
                              })
                            }
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Confirm Password
                          </label>
                          <input
                            type="password"
                            className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                            value={passwordConfirmation}
                            onChange={(e) =>
                              setPasswordConfirmation(e.target.value)
                            }
                            required
                          />
                        </div>
                        <div className="space-y-4">
                          <div className="flex items-center space-x-3">
                            <input
                              type="checkbox"
                              id="terms-signup"
                              className="w-5 h-5 rounded border-gray-300 text-blue-500 focus:ring-blue-500"
                              checked={termsAccepted}
                              onChange={(e) => setTermsAccepted(e.target.checked)}
                              required
                            />
                            <label
                              htmlFor="terms-signup"
                              className="text-gray-700 text-sm"
                            >
                              I agree to the{" "}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setShowTerms(!showTerms);
                                }}
                                className="text-blue-500 hover:underline font-medium"
                              >
                                Terms and Conditions
                              </button>
                            </label>
                          </div>
                          {showTerms && (
                            <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 max-h-48 overflow-y-auto">
                              <h4 className="font-semibold mb-3 text-gray-900">
                                Terms and Conditions
                              </h4>
                              <div className="space-y-3 text-sm text-gray-600">
                                <div>
                                  <p className="font-medium text-gray-900">
                                    1. User Registration and Account
                                  </p>
                                  <ul className="list-disc pl-5 space-y-1 mt-1">
                                    <li>Users must provide accurate and complete information</li>
                                    <li>Users are responsible for maintaining account security</li>
                                    <li>Users must be at least 18 years old to create an account</li>
                                  </ul>
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900">
                                    2. Order and Payment
                                  </p>
                                  <ul className="list-disc pl-5 space-y-1 mt-1">
                                    <li>All orders are subject to restaurant acceptance</li>
                                    <li>Prices are subject to change without notice</li>
                                    <li>Payment information must be valid and accurate</li>
                                  </ul>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="space-y-3">
                          <button
                            type="submit"
                            className="w-full bg-blue-600 text-white py-4 px-6 rounded-xl hover:bg-blue-700 transition-colors font-semibold text-lg"
                          >
                            Create Account
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setFormMode("login");
                              setErrorMessage("");
                            }}
                            className="w-full bg-gray-100 text-gray-800 py-3 px-4 rounded-xl hover:bg-gray-200 transition-colors font-medium"
                          >
                            Already have an account? Sign In
                          </button>
                        </div>
                      </form>
                    </div>
                  )}

                  {formMode === "guestLogin" && (
                    <div className="space-y-6">
                      <div className="text-center">
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Continue as Guest</h2>
                        <p className="text-gray-600">Quick checkout without creating an account</p>
                      </div>
                      
                      {errorMessage && (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                          <p className="text-red-700 text-sm">{errorMessage}</p>
                        </div>
                      )}
                      
                      <form onSubmit={handleGuestLogin} className="space-y-4">
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Email Address
                          </label>
                          <input
                            type="email"
                            className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                            value={userData?.email}
                            onChange={(e) =>
                              setUserData({
                                ...userData,
                                email: e.target.value,
                              })
                            }
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Full Name
                          </label>
                          <input
                            type="text"
                            className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                            value={userData?.customer_name || ""}
                            onChange={(e) =>
                              setUserData({
                                ...userData,
                                customer_name: e.target.value,
                              })
                            }
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Phone Number
                          </label>
                          <input
                            type="tel"
                            className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                            value={userData?.phone_number}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (/^\d{0,10}$/.test(value)) {
                                setUserData({
                                  ...userData,
                                  phone_number: value,
                                });
                              }
                            }}
                            maxLength="10"
                            required
                          />
                        </div>
                        <div className="space-y-4">
                          <div className="flex items-center space-x-3">
                            <input
                              type="checkbox"
                              id="terms-guest"
                              className="w-5 h-5 rounded border-gray-300 text-blue-500 focus:ring-blue-500"
                              checked={termsAccepted}
                              onChange={(e) => setTermsAccepted(e.target.checked)}
                              required
                            />
                            <label
                              htmlFor="terms-guest"
                              className="text-gray-700 text-sm"
                            >
                              I agree to the{" "}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setShowTerms(!showTerms);
                                }}
                                className="text-blue-500 hover:underline font-medium"
                              >
                                Terms and Conditions
                              </button>
                            </label>
                          </div>
                          {showTerms && (
                            <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 max-h-48 overflow-y-auto">
                              <h4 className="font-semibold mb-3 text-gray-900">
                                Terms and Conditions
                              </h4>
                              <div className="space-y-3 text-sm text-gray-600">
                                <div>
                                  <p className="font-medium text-gray-900">
                                    1. User Registration and Account
                                  </p>
                                  <ul className="list-disc pl-5 space-y-1 mt-1">
                                    <li>Users must provide accurate and complete information</li>
                                    <li>Users are responsible for maintaining account security</li>
                                    <li>Users must be at least 18 years old to create an account</li>
                                  </ul>
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900">
                                    2. Order and Payment
                                  </p>
                                  <ul className="list-disc pl-5 space-y-1 mt-1">
                                    <li>All orders are subject to restaurant acceptance</li>
                                    <li>Prices are subject to change without notice</li>
                                    <li>Payment information must be valid and accurate</li>
                                  </ul>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        <button
                          type="submit"
                          className="w-full bg-blue-600 text-white py-4 px-6 rounded-xl hover:bg-blue-700 transition-colors font-semibold text-lg"
                        >
                          Continue as Guest
                        </button>
                      </form>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </>
    </div>
  );
};

export default CustomerDetails;